import type { Place } from '@/types/models';

/**
 * Built-in gazetteer of common intercity origins/destinations.
 *
 * <p>The backend matches rides by coordinates, not by name, so the UI has to resolve a
 * typed place into lat/lng. This is a provider-free implementation of that step, good for
 * the MVP and for local development with no map API key. It sits behind
 * {@link resolvePlace}/{@link suggestPlaces}: swapping in Google Places or Mapbox
 * (autocomplete via the backend's LocationService) changes this file and nothing else.
 */
const CITIES: Array<[string, number, number, string]> = [
  ['Delhi', 28.6139, 77.209, 'Delhi'], ['Chandigarh', 30.7333, 76.7794, 'Chandigarh'],
  ['Mumbai', 19.076, 72.8777, 'Maharashtra'], ['Pune', 18.5204, 73.8567, 'Maharashtra'],
  ['Bengaluru', 12.9716, 77.5946, 'Karnataka'], ['Hyderabad', 17.385, 78.4867, 'Telangana'],
  ['Chennai', 13.0827, 80.2707, 'Tamil Nadu'], ['Kolkata', 22.5726, 88.3639, 'West Bengal'],
  ['Ahmedabad', 23.0225, 72.5714, 'Gujarat'], ['Jaipur', 26.9124, 75.7873, 'Rajasthan'],
  ['Lucknow', 26.8467, 80.9462, 'Uttar Pradesh'], ['Kanpur', 26.4499, 80.3319, 'Uttar Pradesh'],
  ['Nagpur', 21.1458, 79.0882, 'Maharashtra'], ['Indore', 22.7196, 75.8577, 'Madhya Pradesh'],
  ['Bhopal', 23.2599, 77.4126, 'Madhya Pradesh'], ['Patna', 25.5941, 85.1376, 'Bihar'],
  ['Vadodara', 22.3072, 73.1812, 'Gujarat'], ['Surat', 21.1702, 72.8311, 'Gujarat'],
  ['Agra', 27.1767, 78.0081, 'Uttar Pradesh'], ['Amritsar', 31.634, 74.8723, 'Punjab'],
  ['Ludhiana', 30.901, 75.8573, 'Punjab'], ['Dehradun', 30.3165, 78.0322, 'Uttarakhand'],
  ['Haridwar', 29.9457, 78.1642, 'Uttarakhand'], ['Shimla', 31.1048, 77.1734, 'Himachal Pradesh'],
  ['Jammu', 32.7266, 74.857, 'Jammu and Kashmir'], ['Gurugram', 28.4595, 77.0266, 'Haryana'],
  ['Noida', 28.5355, 77.391, 'Uttar Pradesh'], ['Faridabad', 28.4089, 77.3178, 'Haryana'],
  ['Ghaziabad', 28.6692, 77.4538, 'Uttar Pradesh'], ['Panipat', 29.3909, 76.9635, 'Haryana'],
  ['Ambala', 30.3782, 76.7767, 'Haryana'], ['Karnal', 29.6857, 76.9905, 'Haryana'],
  ['Jodhpur', 26.2389, 73.0243, 'Rajasthan'], ['Udaipur', 24.5854, 73.7125, 'Rajasthan'],
  ['Kochi', 9.9312, 76.2673, 'Kerala'], ['Thiruvananthapuram', 8.5241, 76.9366, 'Kerala'],
  ['Coimbatore', 11.0168, 76.9558, 'Tamil Nadu'], ['Visakhapatnam', 17.6868, 83.2185, 'Andhra Pradesh'],
  ['Bhubaneswar', 20.2961, 85.8245, 'Odisha'], ['Guwahati', 26.1445, 91.7362, 'Assam'],
  ['Ranchi', 23.3441, 85.3096, 'Jharkhand'], ['Mohali', 30.7046, 76.7179, 'Punjab'],
  ['Panchkula', 30.6942, 76.8606, 'Haryana'], ['Meerut', 28.9845, 77.7064, 'Uttar Pradesh'],
  ['Varanasi', 25.3176, 82.9739, 'Uttar Pradesh'], ['Nashik', 19.9975, 73.7898, 'Maharashtra'],
  ['Goa', 15.4909, 73.8278, 'Goa'],
  ['Raipur', 21.2514, 81.6296, 'Chhattisgarh'],
  ['Bilaspur', 22.0797, 82.1409, 'Chhattisgarh'],
  ['Jabalpur', 23.1815, 79.9864, 'Madhya Pradesh'],
  ['Gwalior', 26.2183, 78.1828, 'Madhya Pradesh'],
  ['Ujjain', 23.1765, 75.7885, 'Madhya Pradesh'],
  ['Kota', 25.2138, 75.8648, 'Rajasthan'],
  ['Ajmer', 26.4499, 74.6399, 'Rajasthan'],
  ['Bikaner', 28.0229, 73.3119, 'Rajasthan'],
  ['Rajkot', 22.3039, 70.8022, 'Gujarat'],
  ['Bhavnagar', 21.7645, 72.1519, 'Gujarat'],
  ['Jamnagar', 22.4707, 70.0577, 'Gujarat'],
  ['Aurangabad', 19.8762, 75.3433, 'Maharashtra'],
  ['Kolhapur', 16.705, 74.2433, 'Maharashtra'],
  ['Solapur', 17.6599, 75.9064, 'Maharashtra'],
  ['Thane', 19.2183, 72.9781, 'Maharashtra'],
  ['Navi Mumbai', 19.033, 73.0297, 'Maharashtra'],
  ['Mysuru', 12.2958, 76.6394, 'Karnataka'],
  ['Mangaluru', 12.9141, 74.856, 'Karnataka'],
  ['Hubballi', 15.3647, 75.124, 'Karnataka'],
  ['Belagavi', 15.8497, 74.4977, 'Karnataka'],
  ['Vijayawada', 16.5062, 80.648, 'Andhra Pradesh'],
  ['Tirupati', 13.6288, 79.4192, 'Andhra Pradesh'],
  ['Madurai', 9.9252, 78.1198, 'Tamil Nadu'],
  ['Tiruchirappalli', 10.7905, 78.7047, 'Tamil Nadu'],
  ['Salem', 11.6643, 78.146, 'Tamil Nadu'],
  ['Kozhikode', 11.2588, 75.7804, 'Kerala'],
  ['Thrissur', 10.5276, 76.2144, 'Kerala'],
  ['Warangal', 17.9689, 79.5941, 'Telangana'],
  ['Cuttack', 20.4625, 85.8828, 'Odisha'],
  ['Jamshedpur', 22.8046, 86.2029, 'Jharkhand'],
  ['Dhanbad', 23.7957, 86.4304, 'Jharkhand'],
  ['Gaya', 24.7914, 85.0002, 'Bihar'],
  ['Siliguri', 26.7271, 88.3953, 'West Bengal'],
  ['Durgapur', 23.5204, 87.3119, 'West Bengal'],
  ['Prayagraj', 25.4358, 81.8463, 'Uttar Pradesh'],
  ['Gorakhpur', 26.7606, 83.3732, 'Uttar Pradesh'],
  ['Bareilly', 28.367, 79.4304, 'Uttar Pradesh'],
  ['Aligarh', 27.8974, 78.088, 'Uttar Pradesh'],
  ['Moradabad', 28.8386, 78.7733, 'Uttar Pradesh'],
  ['Mathura', 27.4924, 77.6737, 'Uttar Pradesh'],
  ['Jhansi', 25.4484, 78.5685, 'Uttar Pradesh'],
  ['Srinagar', 34.0837, 74.7973, 'Jammu and Kashmir'],
  ['Leh', 34.1526, 77.5771, 'Ladakh'],
  ['Manali', 32.2396, 77.1887, 'Himachal Pradesh'],
  ['Rishikesh', 30.0869, 78.2676, 'Uttarakhand'],
  ['Jalandhar', 31.326, 75.5762, 'Punjab'],
  ['Patiala', 30.34, 76.3869, 'Punjab'],
  ['Bathinda', 30.211, 74.9455, 'Punjab'],
  ['Hisar', 29.1492, 75.7217, 'Haryana'],
  ['Rohtak', 28.8955, 76.6066, 'Haryana'],
  ['Sonipat', 28.9931, 77.0151, 'Haryana'],
  ['Imphal', 24.817, 93.9368, 'Manipur'],
  ['Shillong', 25.5788, 91.8933, 'Meghalaya'],
  ['Agartala', 23.8315, 91.2868, 'Tripura'],
  ['Gangtok', 27.3389, 88.6065, 'Sikkim'],
  ['Puducherry', 11.9416, 79.8083, 'Puducherry'],
  ['Kurukshetra', 29.9695, 76.8783, 'Haryana'],
  ['Yamunanagar', 30.129, 77.2674, 'Haryana'],
  ['Bhiwani', 28.7975, 76.1322, 'Haryana'],
  ['Jind', 29.3162, 76.3149, 'Haryana'],
  ['Kaithal', 29.8015, 76.3998, 'Haryana'],
  ['Sirsa', 29.5349, 75.0288, 'Haryana'],
  ['Fatehabad', 29.5152, 75.4554, 'Haryana'],
  ['Rewari', 28.1928, 76.6192, 'Haryana'],
  ['Mahendragarh', 28.2744, 76.1508, 'Haryana'],
  ['Jhajjar', 28.6079, 76.6565, 'Haryana'],
  ['Palwal', 28.1487, 77.332, 'Haryana'],
  ['Narnaul', 28.0444, 76.1081, 'Haryana'],
  ['Hansi', 29.1023, 75.9626, 'Haryana'],
  ['Thanesar', 29.975, 76.83, 'Haryana'],
  ['Hoshiarpur', 31.5143, 75.9115, 'Punjab'],
  ['Pathankot', 32.2643, 75.6421, 'Punjab'],
  ['Moga', 30.8158, 75.1718, 'Punjab'],
  ['Firozpur', 30.9331, 74.6225, 'Punjab'],
  ['Sangrur', 30.2458, 75.8421, 'Punjab'],
  ['Barnala', 30.3745, 75.5487, 'Punjab'],
  ['Kapurthala', 31.38, 75.38, 'Punjab'],
  ['Rupnagar', 30.966, 76.5331, 'Punjab'],
  ['Fatehgarh Sahib', 30.6446, 76.3945, 'Punjab'],
  ['Abohar', 30.1453, 74.1993, 'Punjab'],
  ['Muktsar', 30.474, 74.516, 'Punjab'],
  ['Solan', 30.9084, 77.0999, 'Himachal Pradesh'],
  ['Kullu', 31.9592, 77.1089, 'Himachal Pradesh'],
  ['Dharamshala', 32.219, 76.3234, 'Himachal Pradesh'],
  ['Mandi', 31.708, 76.932, 'Himachal Pradesh'],
  ['Una', 31.4685, 76.2708, 'Himachal Pradesh'],
  ['Haldwani', 29.2183, 79.513, 'Uttarakhand'],
  ['Roorkee', 29.8543, 77.888, 'Uttarakhand'],
  ['Nainital', 29.3919, 79.4542, 'Uttarakhand'],
  ['Kotdwar', 29.746, 78.523, 'Uttarakhand'],
  ['Udhampur', 32.916, 75.1416, 'Jammu and Kashmir'],
  ['Katra', 32.9916, 74.9455, 'Jammu and Kashmir'],
  ['Saharanpur', 29.968, 77.551, 'Uttar Pradesh'],
  ['Muzaffarnagar', 29.4727, 77.7085, 'Uttar Pradesh'],
  ['Bulandshahr', 28.407, 77.8498, 'Uttar Pradesh'],
  ['Hapur', 28.7306, 77.7759, 'Uttar Pradesh'],
  ['Baghpat', 28.948, 77.218, 'Uttar Pradesh'],
  ['Shamli', 29.45, 77.317, 'Uttar Pradesh'],
  ['Rampur', 28.8152, 79.025, 'Uttar Pradesh'],
  ['Bijnor', 29.3724, 78.1364, 'Uttar Pradesh'],
  ['Firozabad', 27.1591, 78.3957, 'Uttar Pradesh'],
  ['Etawah', 26.7855, 79.0215, 'Uttar Pradesh'],
  ['Shahjahanpur', 27.88, 79.91, 'Uttar Pradesh'],
  ['Sitapur', 27.57, 80.68, 'Uttar Pradesh'],
  ['Faizabad', 26.775, 82.15, 'Uttar Pradesh'],
  ['Ayodhya', 26.7922, 82.1998, 'Uttar Pradesh'],
  ['Azamgarh', 26.0685, 83.1859, 'Uttar Pradesh'],
  ['Mirzapur', 25.146, 82.569, 'Uttar Pradesh'],
  ['Banda', 25.4761, 80.336, 'Uttar Pradesh'],
  ['Raebareli', 26.2345, 81.2409, 'Uttar Pradesh'],
  ['Alwar', 27.553, 76.6346, 'Rajasthan'],
  ['Bharatpur', 27.2152, 77.503, 'Rajasthan'],
  ['Sikar', 27.6094, 75.1399, 'Rajasthan'],
  ['Jhunjhunu', 28.1289, 75.398, 'Rajasthan'],
  ['Sri Ganganagar', 29.9038, 73.8772, 'Rajasthan'],
  ['Bhilwara', 25.3407, 74.6313, 'Rajasthan'],
  ['Pali', 25.7711, 73.3234, 'Rajasthan'],
  ['Chittorgarh', 24.8887, 74.6269, 'Rajasthan'],
  ['Tonk', 26.1663, 75.7885, 'Rajasthan'],
  ['Hanumangarh', 29.5815, 74.3294, 'Rajasthan'],
  ['Churu', 28.2969, 74.9647, 'Rajasthan'],
  ['Jaisalmer', 26.9157, 70.9083, 'Rajasthan'],
  ['Pushkar', 26.49, 74.551, 'Rajasthan'],
  ['Mount Abu', 24.5926, 72.7156, 'Rajasthan'],
  ['Sagar', 23.8388, 78.7378, 'Madhya Pradesh'],
  ['Satna', 24.6005, 80.8322, 'Madhya Pradesh'],
  ['Rewa', 24.5362, 81.3037, 'Madhya Pradesh'],
  ['Ratlam', 23.3315, 75.0367, 'Madhya Pradesh'],
  ['Dewas', 22.9676, 76.0534, 'Madhya Pradesh'],
  ['Khandwa', 21.8245, 76.352, 'Madhya Pradesh'],
  ['Chhindwara', 22.0574, 78.9382, 'Madhya Pradesh'],
  ['Bhilai', 21.2094, 81.4285, 'Chhattisgarh'],
  ['Durg', 21.1904, 81.2849, 'Chhattisgarh'],
  ['Korba', 22.3595, 82.7501, 'Chhattisgarh'],
  ['Gandhinagar', 23.2156, 72.6369, 'Gujarat'],
  ['Anand', 22.5645, 72.9289, 'Gujarat'],
  ['Bharuch', 21.7051, 72.9959, 'Gujarat'],
  ['Junagadh', 21.5222, 70.4579, 'Gujarat'],
  ['Mehsana', 23.588, 72.3693, 'Gujarat'],
  ['Bhuj', 23.242, 69.6669, 'Gujarat'],
  ['Nanded', 19.1383, 77.321, 'Maharashtra'],
  ['Amravati', 20.9374, 77.7796, 'Maharashtra'],
  ['Akola', 20.7002, 77.0082, 'Maharashtra'],
  ['Latur', 18.4088, 76.5604, 'Maharashtra'],
  ['Satara', 17.6805, 74.0183, 'Maharashtra'],
  ['Sangli', 16.8524, 74.5815, 'Maharashtra'],
  ['Ahmednagar', 19.0948, 74.748, 'Maharashtra'],
  ['Jalgaon', 21.0077, 75.5626, 'Maharashtra'],
  ['Dhule', 20.9042, 74.7749, 'Maharashtra'],
  ['Ratnagiri', 16.9902, 73.312, 'Maharashtra'],
  ['Guntur', 16.3067, 80.4365, 'Andhra Pradesh'],
  ['Nellore', 14.4426, 79.9865, 'Andhra Pradesh'],
  ['Kurnool', 15.8281, 78.0373, 'Andhra Pradesh'],
  ['Rajahmundry', 17.0005, 81.804, 'Andhra Pradesh'],
  ['Kakinada', 16.9891, 82.2475, 'Andhra Pradesh'],
  ['Anantapur', 14.6819, 77.6006, 'Andhra Pradesh'],
  ['Nizamabad', 18.6725, 78.0941, 'Telangana'],
  ['Karimnagar', 18.4386, 79.1288, 'Telangana'],
  ['Khammam', 17.2473, 80.1514, 'Telangana'],
  ['Vellore', 12.9165, 79.1325, 'Tamil Nadu'],
  ['Erode', 11.341, 77.7172, 'Tamil Nadu'],
  ['Tirunelveli', 8.7139, 77.7567, 'Tamil Nadu'],
  ['Thanjavur', 10.787, 79.1378, 'Tamil Nadu'],
  ['Tiruppur', 11.1085, 77.3411, 'Tamil Nadu'],
  ['Kanyakumari', 8.0883, 77.5385, 'Tamil Nadu'],
  ['Ooty', 11.4102, 76.695, 'Tamil Nadu'],
  ['Kannur', 11.8745, 75.3704, 'Kerala'],
  ['Kollam', 8.8932, 76.6141, 'Kerala'],
  ['Alappuzha', 9.4981, 76.3388, 'Kerala'],
  ['Palakkad', 10.7867, 76.6548, 'Kerala'],
  ['Kottayam', 9.5916, 76.5222, 'Kerala'],
  ['Davanagere', 14.4644, 75.9218, 'Karnataka'],
  ['Ballari', 15.1394, 76.9214, 'Karnataka'],
  ['Shivamogga', 13.9299, 75.5681, 'Karnataka'],
  ['Tumakuru', 13.3379, 77.1173, 'Karnataka'],
  ['Udupi', 13.3409, 74.7421, 'Karnataka'],
  ['Kalaburagi', 17.3297, 76.8343, 'Karnataka'],
  ['Vijayapura', 16.8302, 75.71, 'Karnataka'],
  ['Bhagalpur', 25.2425, 86.9842, 'Bihar'],
  ['Muzaffarpur', 26.1209, 85.3647, 'Bihar'],
  ['Darbhanga', 26.1542, 85.8918, 'Bihar'],
  ['Purnia', 25.7771, 87.4753, 'Bihar'],
  ['Arrah', 25.5541, 84.6603, 'Bihar'],
  ['Bokaro', 23.6693, 86.1511, 'Jharkhand'],
  ['Deoghar', 24.4764, 86.6953, 'Jharkhand'],
  ['Hazaribagh', 23.9925, 85.3637, 'Jharkhand'],
  ['Asansol', 23.6739, 86.9524, 'West Bengal'],
  ['Howrah', 22.5958, 88.2636, 'West Bengal'],
  ['Kharagpur', 22.346, 87.232, 'West Bengal'],
  ['Bardhaman', 23.2324, 87.8615, 'West Bengal'],
  ['Rourkela', 22.2604, 84.8536, 'Odisha'],
  ['Sambalpur', 21.4669, 83.9812, 'Odisha'],
  ['Puri', 19.8135, 85.8312, 'Odisha'],
  ['Berhampur', 19.3149, 84.7941, 'Odisha'],
  ['Dibrugarh', 27.4728, 94.912, 'Assam'],
  ['Jorhat', 26.7509, 94.2037, 'Assam'],
  ['Silchar', 24.8333, 92.7789, 'Assam'],
  ['Tezpur', 26.6528, 92.7926, 'Assam'],
  ['Itanagar', 27.0844, 93.6053, 'Arunachal Pradesh'],
  ['Kohima', 25.6751, 94.1086, 'Nagaland'],
  ['Aizawl', 23.7271, 92.7176, 'Mizoram'],
  ['Dimapur', 25.9044, 93.7266, 'Nagaland'],
];

const PLACES: Place[] = CITIES.map(([shortName, latitude, longitude, region]) => ({
  shortName, latitude, longitude, formattedAddress: `${shortName}, ${region}, India`,
}));

export const placeNames = (): string[] => PLACES.map((p) => p.shortName);

export function resolvePlace(input: string): Place | undefined {
  const needle = input.trim().toLowerCase();
  return PLACES.find((p) => p.shortName.toLowerCase() === needle);
}

export function suggestPlaces(input: string, limit = 6): Place[] {
  const needle = input.trim().toLowerCase();
  if (!needle) return PLACES.slice(0, limit);
  return PLACES.filter((p) => p.shortName.toLowerCase().startsWith(needle)).slice(0, limit);
}
